import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Empty,
  EmptyIcon,
  EmptyText,
  EmptyTitle,
  ShellIconWhatsApp,
  SplitWorkbenchLayout,
  useBreakpoint,
  useIsPhone,
} from '@zrsystem/ui';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

import { uploadWhatsAppInboxAttachment } from '@/features/notification/api/whatsapp-inbox-attachments.api.js';
import { WhatsAppInboxChatRail } from '@/features/notification/components/whatsapp-inbox-chat-rail.js';
import { WhatsAppInboxThread } from '@/features/notification/components/whatsapp-inbox-thread.js';
import { useSendWhatsAppInboxMessage } from '@/features/notification/hooks/use-send-whatsapp-inbox-message.js';
import { useWhatsAppInboxChats } from '@/features/notification/hooks/use-whatsapp-inbox-chats.js';
import {
  WHATSAPP_INBOX_MESSAGES_PAGE_SIZE,
  appendOptimisticWhatsAppInboxMessage,
  optimisticWhatsAppInboxAudioMessage,
  optimisticWhatsAppInboxMediaMessage,
  useWhatsAppInboxMessages,
} from '@/features/notification/hooks/use-whatsapp-inbox-messages.js';
import { bindWhatsAppInboxRealtimeSession } from '@/features/notification/lib/whatsapp-inbox-realtime-session.js';
import { acknowledgeWhatsAppInboxChat } from '@/features/notification/lib/whatsapp-inbox-unread.js';
import '@/features/notification/whatsapp-inbox.css';
import { HttpError } from '@/shared/api/types.js';
import { ContextualFeedback } from '@/shared/feedback/index.js';
import { translateError } from '@/shared/i18n/error-messages.js';

function inboxMediaBody(
  storageKey: string,
  file: File,
  caption: string | undefined,
): {
  storageKey: string;
  filename: string;
  contentType: string;
  caption?: string;
} {
  return {
    storageKey,
    filename: file.name,
    contentType: file.type.length > 0 ? file.type : 'application/octet-stream',
    ...(caption !== undefined && caption.length > 0 ? { caption } : {}),
  };
}

function inboxAttachErrorMessage(error: unknown): string {
  if (error instanceof HttpError) {
    return translateError(error.code);
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'Falha ao enviar anexo';
}

export interface WhatsAppInboxPresenterProps {
  readonly placeId: string;
  readonly onNavigateToSettings?: () => void;
  /** List, then the open conversation. Used by the header side panel. */
  readonly stacked?: boolean;
}

export function WhatsAppInboxPresenter({
  placeId,
  onNavigateToSettings,
  stacked = false,
}: WhatsAppInboxPresenterProps): ReactElement {
  const isPhone = useIsPhone();
  const compact = stacked || isPhone;
  const breakpoint = useBreakpoint();
  const asideWidth = breakpoint === 'tablet' ? 260 : 320;

  const queryClient = useQueryClient();
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [mobilePane, setMobilePane] = useState<'rail' | 'thread'>('rail');
  const [chatSearch, setChatSearch] = useState('');
  const [composerDraft, setComposerDraft] = useState('');
  const [composerError, setComposerError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<
    { file: File; previewUrl: string; kind: 'image' | 'video' | 'document' }[]
  >([]);
  const [pendingIndex, setPendingIndex] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [socketErrorMessage, setSocketErrorMessage] = useState<string | null>(null);

  const chatsQuery = useWhatsAppInboxChats(placeId);
  const messagesQuery = useWhatsAppInboxMessages(placeId, selectedChatId);
  const sendMessage = useSendWhatsAppInboxMessage(placeId, selectedChatId);

  useEffect(() => {
    return bindWhatsAppInboxRealtimeSession({
      selectedChatId,
      onError: setSocketErrorMessage,
    });
  }, [selectedChatId]);

  const chats = chatsQuery.data?.chats ?? [];
  const selectedChat = useMemo(
    () => chats.find((chat) => chat.id === selectedChatId) ?? null,
    [chats, selectedChatId],
  );

  const listErrorMessage =
    chatsQuery.error instanceof HttpError ? translateError(chatsQuery.error.code) : null;

  const accountUnavailable = chatsQuery.data?.accountUnavailable ?? false;

  const handleSelectChat = useCallback(
    (chatId: string) => {
      const chat = chatsQuery.data?.chats.find((row) => row.id === chatId);
      if (chat !== undefined) {
        acknowledgeWhatsAppInboxChat(queryClient, placeId, chat);
      }
      setSelectedChatId(chatId);
      setComposerDraft('');
      setComposerError(null);
      setPendingAttachments((current) => {
        for (const item of current) URL.revokeObjectURL(item.previewUrl);
        return [];
      });
      setPendingIndex(0);
      setMobilePane('thread');
    },
    [chatsQuery.data?.chats, placeId, queryClient],
  );

  const handleBackToRail = useCallback(() => {
    setMobilePane('rail');
    setComposerDraft('');
    setComposerError(null);
    setPendingAttachments((current) => {
      for (const item of current) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    setPendingIndex(0);
  }, []);

  const handleSubmitText = useCallback(async () => {
    const text = composerDraft.trim();
    if (text.length === 0 || selectedChatId === null) {
      return;
    }

    setComposerError(null);
    try {
      await sendMessage.mutateAsync({ text });
      setComposerDraft('');
    } catch (error) {
      if (error instanceof HttpError) {
        setComposerError(translateError(error.code));
      } else if (error instanceof Error) {
        setComposerError(error.message);
      } else {
        setComposerError('Falha ao enviar mensagem');
      }
    }
  }, [composerDraft, selectedChatId, sendMessage]);

  const clearPendingAttachment = useCallback(() => {
    setPendingAttachments((current) => {
      for (const item of current) URL.revokeObjectURL(item.previewUrl);
      return [];
    });
    setPendingIndex(0);
  }, []);

  const pendingCountRef = useRef(0);
  pendingCountRef.current = pendingAttachments.length;

  const appendPendingAttachments = useCallback((files: readonly File[]) => {
    if (files.length === 0) return;
    const staged = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
      kind: file.type.startsWith('image/')
        ? ('image' as const)
        : file.type.startsWith('video/') || /\.(mp4|mov|webm|mkv|3gp)$/i.test(file.name)
          ? ('video' as const)
          : ('document' as const),
    }));
    const nextIndex = pendingCountRef.current + staged.length - 1;
    pendingCountRef.current += staged.length;
    setPendingIndex(nextIndex);
    setPendingAttachments((current) => [...current, ...staged]);
    setComposerError(null);
  }, []);

  const handleAttachFile = useCallback(
    async (file: File): Promise<boolean> => {
      if (selectedChatId === null) {
        return false;
      }
      const chatId = selectedChatId;

      if (file.type.startsWith('image/') || !file.type.startsWith('audio/')) {
        appendPendingAttachments([file]);
        return true;
      }

      setIsUploading(true);
      setComposerError(null);
      const caption = composerDraft.trim();
      try {
        const uploaded = await uploadWhatsAppInboxAttachment(placeId, file);
        const sent = await sendMessage.mutateAsync({
          media: inboxMediaBody(uploaded.storageKey, file, undefined),
        });
        appendOptimisticWhatsAppInboxMessage(
          queryClient,
          placeId,
          chatId,
          optimisticWhatsAppInboxAudioMessage({
            providerMessageId: sent.providerMessageId ?? `local-${Date.now()}`,
            chatId,
            file,
          }),
        );
        setComposerDraft('');
      } catch (error) {
        setComposerError(inboxAttachErrorMessage(error));
        return false;
      } finally {
        setIsUploading(false);
      }

      if (caption.length > 0) {
        try {
          await sendMessage.mutateAsync({ text: caption });
        } catch (error) {
          setComposerDraft(caption);
          setComposerError(inboxAttachErrorMessage(error));
        }
      }
      return true;
    },
    [appendPendingAttachments, composerDraft, placeId, queryClient, selectedChatId, sendMessage],
  );

  const handleConfirmPendingAttachment = useCallback(
    async (captions: readonly (string | undefined)[]) => {
      if (pendingAttachments.length === 0 || selectedChatId === null) {
        return;
      }

      const chatId = selectedChatId;
      setIsUploading(true);
      setComposerError(null);
      const kept: typeof pendingAttachments = [];
      let failed = false;
      for (let index = 0; index < pendingAttachments.length; index += 1) {
        const item = pendingAttachments[index];
        if (item === undefined) continue;
        if (failed) {
          kept.push(item);
          continue;
        }
        try {
          const uploaded = await uploadWhatsAppInboxAttachment(placeId, item.file);
          const caption = captions[index];
          const sent = await sendMessage.mutateAsync({
            media: inboxMediaBody(uploaded.storageKey, item.file, caption),
          });
          appendOptimisticWhatsAppInboxMessage(
            queryClient,
            placeId,
            chatId,
            optimisticWhatsAppInboxMediaMessage({
              providerMessageId: sent.providerMessageId ?? `local-${Date.now()}-${index}`,
              chatId,
              file: item.file,
              previewUrl: item.previewUrl,
              contentType: uploaded.contentType,
              ...(caption !== undefined ? { caption } : {}),
            }),
          );
        } catch (error) {
          failed = true;
          setComposerError(inboxAttachErrorMessage(error));
          kept.push(item);
        }
      }
      setPendingAttachments(kept);
      setPendingIndex(0);
      setIsUploading(false);
    },
    [pendingAttachments, placeId, queryClient, selectedChatId, sendMessage],
  );

  const handleLoadMore = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      await messagesQuery.fetchMore();
    } finally {
      setIsLoadingMore(false);
    }
  }, [messagesQuery]);

  const canLoadMore = (messagesQuery.data?.length ?? 0) >= WHATSAPP_INBOX_MESSAGES_PAGE_SIZE;

  const emptyState =
    accountUnavailable && onNavigateToSettings !== undefined ? (
      <Empty>
        <EmptyIcon>
          <ShellIconWhatsApp size={22} />
        </EmptyIcon>
        <EmptyTitle>WhatsApp indisponível</EmptyTitle>
        <EmptyText>Conecte a conta WhatsApp da unidade para ver os chats.</EmptyText>
        <Button type="button" variant="primary" size="sm" onClick={onNavigateToSettings}>
          Ir para configurações
        </Button>
      </Empty>
    ) : undefined;

  const rail = (
    <WhatsAppInboxChatRail
      chats={chats}
      selectedChatId={selectedChatId}
      isLoading={chatsQuery.isPending}
      errorMessage={listErrorMessage}
      searchQuery={chatSearch}
      onSearchQueryChange={setChatSearch}
      emptyState={emptyState}
      onSelectChat={handleSelectChat}
    />
  );

  const placeholder = (
    <div className="whatsapp-inbox__placeholder">
      <ShellIconWhatsApp size={36} />
      <EmptyText>Selecione um chat para ver as mensagens</EmptyText>
    </div>
  );

  const threadPanel =
    selectedChat !== null
      ? (() => {
          const threadProps = {
            placeId,
            chat: selectedChat,
            messages: messagesQuery.data ?? [],
            isLoading: messagesQuery.isPending,
            errorMessage:
              messagesQuery.isError && messagesQuery.error instanceof HttpError
                ? translateError(messagesQuery.error.code)
                : null,
            isPhone: compact,
            isSending: sendMessage.isPending,
            isUploading,
            composerDraft,
            composerError,
            canLoadMore,
            isLoadingMore,
            onLoadMore: handleLoadMore,
            onDraftChange: setComposerDraft,
            onSubmitText: () => {
              void handleSubmitText();
            },
            onSubmitAudio: (file: File) => handleAttachFile(file),
            onComposerError: setComposerError,
            onAttachFile: (file: File) => {
              void handleAttachFile(file);
            },
            pendingAttachments: pendingAttachments.map((item) => ({
              kind: item.kind,
              previewUrl: item.previewUrl,
              fileName: item.file.name,
              fileSize: item.file.size,
              contentType: item.file.type.length > 0 ? item.file.type : 'application/octet-stream',
            })),
            selectedAttachmentIndex: pendingIndex,
            onSelectPendingAttachment: setPendingIndex,
            onCancelPendingAttachment: clearPendingAttachment,
            onConfirmPendingAttachment: (captions: readonly (string | undefined)[]) => {
              void handleConfirmPendingAttachment(captions);
            },
            onAddPendingFiles: appendPendingAttachments,
          };

          return compact ? (
            <WhatsAppInboxThread {...threadProps} onBack={handleBackToRail} />
          ) : (
            <WhatsAppInboxThread {...threadProps} />
          );
        })()
      : null;

  return (
    <div className="whatsapp-inbox">
      {socketErrorMessage !== null ? (
        <ContextualFeedback variant="danger" message={socketErrorMessage} />
      ) : null}
      {compact ? (
        mobilePane === 'rail' || selectedChat === null ? (
          rail
        ) : (
          threadPanel
        )
      ) : (
        <SplitWorkbenchLayout
          asideWidth={asideWidth}
          aside={rail}
          className="whatsapp-inbox__split"
        >
          {selectedChat === null ? placeholder : threadPanel}
        </SplitWorkbenchLayout>
      )}
    </div>
  );
}
